<template>
  <div class="flex h-screen overflow-hidden">
    <!-- Mobile Header with Toggle Button -->
    <header class="lg:hidden fixed top-0 left-0 right-0 h-16 bg-gradient-to-br from-slate-900 to-slate-800 text-white z-30 flex items-center justify-between px-4 shadow-lg">
      <div class="flex items-center gap-3">
        <button 
          @click="toggleMobileMenu"
          class="p-2 hover:bg-white/10 rounded-lg transition-colors"
          aria-label="Toggle navigation"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path v-if="!isMobileMenuOpen" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path>
            <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
        <h1 class="text-lg font-bold tracking-tight">Voice AI Landscape</h1>
      </div>
      <span class="text-xs text-slate-400">{{ totalPlatforms }} platforms</span>
    </header>

    <!-- Mobile Overlay Backdrop -->
    <div 
      v-if="isMobileMenuOpen" 
      @click="closeMobileMenu"
      class="lg:hidden fixed inset-0 bg-black/50 z-40 backdrop-blur-sm transition-opacity"
    ></div>

    <!-- Left Sidebar Navigation -->
    <Sidebar 
      :active-section="activeSection"
      :active-category="activeCategory"
      :expanded-groups="expandedGroups"
      :is-mobile-open="isMobileMenuOpen"
      @toggle-group="toggleGroup"
      @scroll-to-section="handleScrollToSection"
      @close-mobile="closeMobileMenu"
    />

    <!-- Main Content Area -->
    <main class="flex-1 overflow-y-auto scroll-container bg-slate-50/50 relative lg:pt-0 pt-16" ref="mainContainer">
      <div class="max-w-5xl mx-auto p-4 lg:p-8 pb-24">

        <!-- Header -->
        <header class="mb-8 lg:mb-12 text-center py-4 lg:py-8">
          <h1 class="text-2xl lg:text-4xl font-bold mb-4 gradient-text">Voice AI Competitive Analysis</h1>
          <p class="text-slate-600 max-w-2xl mx-auto text-sm lg:text-lg">
            Comprehensive overview of telephony-backed voice AI platforms, direct competitors, and vertical-specific applications with pricing intelligence.
          </p>
        </header>

        <!-- MARKET ANALYSIS SECTION -->
        <section id="section-market-analysis" class="mb-16">
          <div class="flex items-center gap-3 mb-8">
            <div class="w-1 h-8 bg-indigo-500 rounded-full"></div>
            <h2 class="text-2xl font-bold text-slate-900">Market Analysis</h2>
            <span class="px-3 py-1 bg-indigo-100 text-indigo-700 text-xs font-semibold rounded-full">Industry Overview</span>
          </div>

          <div class="bg-white rounded-2xl p-4 lg:p-8 border border-slate-200 shadow-sm mb-8">
            <h3 class="text-lg font-bold text-slate-900 mb-4">Key Market Terms & Industry Context</h3>
            
            <div class="grid md:grid-cols-2 gap-6 mb-6">
              <div class="p-4 bg-slate-50 rounded-lg">
                <h4 class="font-semibold text-slate-800 mb-2">Market Size & Growth</h4>
                <p class="text-sm text-slate-600">Voice AI agents market projected to grow from <strong>$2.4B (2024)</strong> to <strong>$47.5B (2034)</strong> at 34.8% CAGR. VC investment grew 7x from $315M (2022) to $2.1B (2024).</p>
              </div>
              <div class="p-4 bg-slate-50 rounded-lg">
                <h4 class="font-semibold text-slate-800 mb-2">Critical Performance Metrics</h4>
                <p class="text-sm text-slate-600">Enterprise procurement prioritizes: <strong>sub-800ms latency</strong>, transparent SLAs, <strong>sub-500ms response times</strong>, and measurable first-call-resolution rates.</p>
              </div>
            </div>

            <div class="grid md:grid-cols-3 gap-4 mb-6">
              <div class="p-4 bg-indigo-50 rounded-lg border border-indigo-100">
                <h4 class="font-semibold text-indigo-900 mb-2">Pricing Models</h4>
                <p class="text-sm text-indigo-800">Market ranges $0.05-$0.22/min base rates. Total cost typically 2-4x base due to add-ons (LLM, STT, telephony).</p>
              </div>
              <div class="p-4 bg-emerald-50 rounded-lg border border-emerald-100">
                <h4 class="font-semibold text-emerald-900 mb-2">Compliance Standards</h4>
                <p class="text-sm text-emerald-800">HIPAA, SOC2 Type I & II, GDPR now table stakes for enterprise adoption.</p>
              </div>
              <div class="p-4 bg-amber-50 rounded-lg border border-amber-100">
                <h4 class="font-semibold text-amber-900 mb-2">Platform Categories</h4>
                <p class="text-sm text-amber-800">Workflow-first, developer toolkits, voice synthesis layers, conversation design tools, enterprise CCaaS.</p>
              </div>
            </div>

            <div class="border-t border-slate-200 pt-6">
              <h4 class="font-semibold text-slate-800 mb-3">VoiceHub Positioning Matrix</h4>
              <div class="overflow-x-auto">
                <table class="w-full text-sm">
                  <thead class="bg-slate-100">
                    <tr>
                      <th class="px-4 py-2 text-left font-semibold text-slate-700">Feature Category</th>
                      <th class="px-4 py-2 text-left font-semibold text-slate-700">VoiceHub</th>
                      <th class="px-4 py-2 text-left font-semibold text-slate-700">Market Average</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-slate-200">
                    <tr>
                      <td class="px-4 py-3 font-medium text-slate-800">Multi-Tenancy</td>
                      <td class="px-4 py-3 text-emerald-600 font-semibold">Two-layer (Workspaces + White-label)</td>
                      <td class="px-4 py-3 text-slate-600">Single-tenant or basic workspaces</td>
                    </tr>
                    <tr>
                      <td class="px-4 py-3 font-medium text-slate-800">Telephony Model</td>
                      <td class="px-4 py-3 text-emerald-600 font-semibold">Built-in (Twilio/VAPI)</td>
                      <td class="px-4 py-3 text-slate-600">BYO or bundled</td>
                    </tr>
                    <tr>
                      <td class="px-4 py-3 font-medium text-slate-800">Pricing Transparency</td>
                      <td class="px-4 py-3 text-amber-600 font-semibold">Custom/Enterprise</td>
                      <td class="px-4 py-3 text-slate-600">$0.07-0.08/min published</td>
                    </tr>
                    <tr>
                      <td class="px-4 py-3 font-medium text-slate-800">Latency Performance</td>
                      <td class="px-4 py-3 text-slate-600">Not published</td>
                      <td class="px-4 py-3 text-slate-600">620-950ms (competitor range)</td>
                    </tr>
                    <tr>
                      <td class="px-4 py-3 font-medium text-slate-800">Follow-up Automation</td>
                      <td class="px-4 py-3 text-emerald-600 font-semibold">Native SMS/Email templates</td>
                      <td class="px-4 py-3 text-slate-600">Via integrations (Zapier/webhooks)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        <div class="section-divider my-12"></div>

        <!-- DIRECT COMPETITORS SECTION -->
        <section id="section-direct" class="mb-16">
          <div class="flex items-center gap-3 mb-8">
            <div class="w-1 h-8 bg-red-500 rounded-full"></div>
            <h2 class="text-2xl font-bold text-slate-900">Direct Competitors</h2>
            <span class="px-3 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded-full">High Priority</span>
            <span class="text-sm text-slate-500 font-normal">({{ directCompetitors.length }} platforms)</span>
          </div>

          <div class="grid gap-6">
            <CompetitorCard 
              v-for="comp in directCompetitors" 
              :key="comp.id"
              :id="comp.id"
              :comp="comp"
            />
          </div>
        </section>

        <div class="section-divider my-12"></div>

        <!-- VERTICAL APPLICATIONS -->
        <div 
          v-for="(category, catIdx) in categories" 
          :key="catIdx" 
          :id="'section-' + category.id"
          class="mb-16"
        >
          <div class="flex items-center gap-3 mb-8">
            <div class="w-1 h-8 rounded-full" :class="getCategoryColor(catIdx, true)"></div>
            <h2 class="text-2xl font-bold text-slate-900">{{ category.name }}</h2>
            <span class="text-sm text-slate-500 font-normal">({{ category.apps.length }} platforms)</span>
          </div>

          <div class="grid gap-6">
            <div 
              v-for="app in category.apps" 
              :key="app.id"
              :id="app.id"
              class="competitor-card bg-white rounded-xl p-6 border border-slate-200 shadow-sm hover:border-blue-300 transition-colors"
            >
              <div class="flex justify-between items-start mb-3">
                <div>
                  <div class="flex items-center gap-2 mb-1">
                    <h3 class="text-lg font-bold text-slate-900">{{ app.name }}</h3>
                    <span v-if="app.pricing" class="price-tag text-white px-2 py-0.5 rounded text-xs font-bold">
                      {{ app.pricing }}
                    </span>
                  </div>
                  <a :href="app.website.startsWith('http') ? app.website : 'https://' + app.website" target="_blank" class="text-sm text-blue-600 hover:underline">
                    {{ app.website.replace('https://', '').replace('http://', '') }}
                  </a>
                </div>
                <span class="text-xs px-2 py-1 bg-slate-100 text-slate-600 rounded border border-slate-200">
                  {{ app.industry }}
                </span>
              </div>

              <div class="flex flex-wrap gap-1.5 mb-3">
                <span v-for="feature in app.features" :key="feature" class="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded border border-blue-100">
                  {{ feature }}
                </span>
              </div>

              <p class="text-slate-600 text-sm leading-relaxed mb-3">{{ app.summary }}</p>

              <div v-if="app.pricingDetails" class="mt-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div class="text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1">
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  Pricing Details
                </div>
                <p class="text-xs text-slate-600">{{ app.pricingDetails }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <footer class="mt-16 pt-8 border-t border-slate-200 text-center text-slate-500 text-sm">
          <p>Competitive analysis compiled from public documentation and platform listings.</p>
          <p class="mt-1">Pricing subject to change; verify with vendors directly.</p>
        </footer>

      </div>
    </main>

  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import Sidebar from './components/Sidebar.vue';
import CompetitorCard from './components/CompetitorCard.vue';
import { directCompetitors, categories } from './data/competitors.js';

const mainContainer = ref(null);
const activeSection = ref('');
const activeCategory = ref('direct');
const isMobileMenuOpen = ref(false);

// Track expanded state of groups
const expandedGroups = ref({
  direct: true, // Start expanded
  ecommerce: false,
  healthcare: false,
  realestate: false,
  hospitality: false,
  homeservices: false,
  callcenters: false,
  education: false
});

const totalPlatforms = computed(() => {
  return directCompetitors.length + categories.reduce((acc, cat) => acc + cat.apps.length, 0);
});

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
  // Prevent body scroll when menu is open
  if (isMobileMenuOpen.value) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = '';
  }
};

const closeMobileMenu = () => {
  isMobileMenuOpen.value = false;
  document.body.style.overflow = '';
};

const handleScrollToSection = (sectionId) => {
  scrollToSection(sectionId);
  // Close mobile menu after navigation
  if (isMobileMenuOpen.value) {
    closeMobileMenu();
  }
};

const toggleGroup = (groupId) => {
  expandedGroups.value[groupId] = !expandedGroups.value[groupId];
};

const getCategoryColor = (idx, isBg = false) => {
  const colors = ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500', 'bg-pink-500', 'bg-indigo-500', 'bg-teal-500'];
  const bgColors = ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500', 'bg-pink-500', 'bg-indigo-500', 'bg-teal-500'];
  return isBg ? bgColors[idx % bgColors.length] : colors[idx % bgColors.length];
};

// FIXED: Proper scroll function that targets the main container
const scrollToSection = (sectionId) => {
  if (!mainContainer.value) return;
  
  const element = document.getElementById(sectionId);
  if (element) {
    // Calculate position relative to the main container
    const containerTop = mainContainer.value.getBoundingClientRect().top;
    const elementTop = element.getBoundingClientRect().top;
    const scrollTop = mainContainer.value.scrollTop;
    const offset = 20; // Small offset from top
    
    const targetScroll = scrollTop + (elementTop - containerTop) - offset;
    
    mainContainer.value.scrollTo({
      top: targetScroll,
      behavior: 'smooth'
    });
    
    activeSection.value = sectionId;
    
    // Determine category from section ID
    const comp = directCompetitors.find(c => c.id === sectionId);
    if (comp) {
      activeCategory.value = 'direct';
      if (!expandedGroups.value.direct) {
        expandedGroups.value.direct = true;
      }
    } else {
      for (const cat of categories) {
        const app = cat.apps.find(a => a.id === sectionId);
        if (app) {
          activeCategory.value = cat.id;
          if (!expandedGroups.value[cat.id]) {
            expandedGroups.value[cat.id] = true;
          }
          break;
        }
      }
    }
  }
};

const handleScroll = () => {
  if (!mainContainer.value) return;
  
  const scrollTop = mainContainer.value.scrollTop;
  const containerHeight = mainContainer.value.clientHeight;
  const scrollCenter = scrollTop + (containerHeight / 3);
  
  // Check all section elements
  const allIds = [
    ...directCompetitors.map(c => c.id),
    ...categories.flatMap(c => c.apps.map(a => a.id))
  ];
  
  let currentSection = '';
  let minDistance = Infinity;
  
  for (const id of allIds) {
    const element = document.getElementById(id);
    if (element) {
      const elementTop = element.offsetTop;
      const distance = Math.abs(scrollCenter - elementTop);
      
      if (distance < minDistance) {
        minDistance = distance;
        currentSection = id;
      }
    }
  }
  
  if (currentSection && currentSection !== activeSection.value) {
    activeSection.value = currentSection;
    
    // Update active category
    const comp = directCompetitors.find(c => c.id === currentSection);
    if (comp) {
      activeCategory.value = 'direct';
    } else {
      for (const cat of categories) {
        if (cat.apps.some(a => a.id === currentSection)) {
          activeCategory.value = cat.id;
          break;
        }
      }
    }
  }
};

onMounted(() => {
  if (mainContainer.value) {
    mainContainer.value.addEventListener('scroll', handleScroll, { passive: true });
    // Initial scroll position
    setTimeout(() => {
      handleScroll();
    }, 100);
  }
});

onUnmounted(() => {
  if (mainContainer.value) {
    mainContainer.value.removeEventListener('scroll', handleScroll);
  }
  // Clean up body overflow
  document.body.style.overflow = '';
});
</script>
